import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Alert components with Hyperkit styling
 * Supports semantic variants for different message types
 */
const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default:
          'bg-white text-grey-900 border-grey-200 dark:bg-grey-900 dark:text-grey-50 dark:border-grey-800',
        info:
          'bg-blue-50 text-blue-900 border-blue-200 [&>svg]:text-blue-500 dark:bg-blue-950/50 dark:text-blue-100 dark:border-blue-800',
        success:
          'bg-green-50 text-green-900 border-green-200 [&>svg]:text-green-500 dark:bg-green-950/50 dark:text-green-100 dark:border-green-800',
        warning:
          'bg-orange-50 text-orange-900 border-orange-200 [&>svg]:text-orange-500 dark:bg-orange-950/50 dark:text-orange-100 dark:border-orange-800',
        destructive:
          'bg-red-50 text-red-900 border-red-200 [&>svg]:text-red-500 dark:bg-red-950/50 dark:text-red-100 dark:border-red-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription, alertVariants };
